import { useEffect, useRef, useState } from "react";
import { useRoomContext } from "@livekit/components-react";
import { RoomEvent } from "livekit-client";

// A CourtsApp MCP tool that ships an MCP Apps HTML card: which card, plus the
// tool input and full result the card is given (see cards/mcp-app).
export interface McpUiPayload {
  resourceUri: string;
  input: Record<string, unknown>;
  result: Record<string, unknown>;
}

export interface ReceivedToolResult {
  tool: string;
  receivedAt: number;
  data: unknown;
  ui?: McpUiPayload;
  exiting?: boolean;
  // When the real result reached the widget (client clock), set the moment it
  // arrives, even while the skeleton is held for MIN_SKELETON_MS. Her narration
  // ("let me check...") comes before it and her reply after it, which is how the
  // timeline orders the two around the card. Undefined while still loading.
  resultAt?: number;
}

const TOPIC = "courtney.toolResult";
// Same envelope as a LiveKit text stream: payloads over a data packet's ~15 KB
// (MCP card results run 30-90 KB) come this way (src/tools/emit.py).
const STREAM_TOPIC = "courtney.toolResult.stream";
export const DUP_WINDOW_MS = 10_000;
// Hold a result until its skeleton has shown at least this long, so the loader
// reads as deliberate instead of a flash (splits the difference between an
// instant pop-in and waiting on the agent's reply).
export const MIN_SKELETON_MS = 1100;

// Fingerprint of the rendered cards, for the message list's stick-to-bottom
// signal. Card COUNT isn't enough: the skeleton and its real result share one
// slot (same tool + receivedAt, array length unchanged), so a count-based signal
// misses the morph — the card grows from skeleton height to full height and the
// view stays put until the next message. The loading flag makes that swap visible.
export const cardsSignal = (results: ReceivedToolResult[]): string =>
  results
    .map(
      (r) =>
        `${r.tool}${r.receivedAt}${(r.data as Record<string, unknown>)?.loading ? "L" : "D"}${r.exiting ? "X" : ""}`,
    )
    .join(",");

export function asUiPayload(value: unknown): McpUiPayload | undefined {
  if (!value || typeof value !== "object") return undefined;
  const v = value as Record<string, unknown>;
  if (typeof v.resourceUri !== "string" || !v.resourceUri.startsWith("ui://")) return undefined;
  if (!v.result || typeof v.result !== "object") return undefined;
  return {
    resourceUri: v.resourceUri,
    input: v.input && typeof v.input === "object" ? (v.input as Record<string, unknown>) : {},
    result: v.result as Record<string, unknown>,
  };
}

function isEnvelope(value: unknown): value is { tool: string; data: unknown; ui?: unknown } {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return typeof v.tool === "string" && v.tool.length > 0 && "data" in v;
}

export function isDuplicateEnvelope(
  prev: { key: string; ts: number } | null,
  candidateKey: string,
  now: number,
  windowMs: number = DUP_WINDOW_MS,
): boolean {
  return prev !== null && prev.key === candidateKey && now - prev.ts <= windowMs;
}

export function useToolResults(): ReceivedToolResult[] {
  const room = useRoomContext();
  const [results, setResults] = useState<ReceivedToolResult[]>([]);
  // The agent's LLM occasionally fires the same tool twice in one turn,
  // which lands two identical cards in the timeline. Compare the raw envelope
  // text against the previous one within a short window and drop repeats.
  const lastEnvelopeRef = useRef<{ key: string; ts: number } | null>(null);
  // tool → when its in-flight skeleton first appeared, to enforce MIN_SKELETON_MS.
  const skeletonStartRef = useRef<Record<string, number>>({});

  useEffect(() => {
    if (!room) return;

    const handleText = (text: string) => {
      try {
        const parsed: unknown = JSON.parse(text);
        if (!isEnvelope(parsed)) {
          // TODO: surface to telemetry when wiring is added.
          console.warn("[useToolResults] rejected malformed envelope");
          return;
        }
        const now = Date.now();
        const key = `${parsed.tool}|${text}`;
        if (isDuplicateEnvelope(lastEnvelopeRef.current, key, now)) {
          console.warn("[useToolResults] dropped duplicate envelope", {
            tool: parsed.tool,
          });
          return;
        }
        lastEnvelopeRef.current = { key, ts: now };
        const isLoading =
          (parsed.data as Record<string, unknown>)?.loading === true;
        const commit = () =>
          setResults((prev) => {
          // Any incoming frame for a tool clears that tool's in-flight skeleton
          // (the `{loading:true}` placeholder), so it swaps to the real card —
          // or clears — in place. Shared across every card type.
          const isLoading =
            (parsed.data as Record<string, unknown>)?.loading === true;
          // Reuse the in-flight skeleton's slot timestamp so the real result
          // gets the SAME timeline key — React keeps the card container mounted
          // and just morphs its content (skeleton → cards) in place, instead of
          // remount → collapse → expand (the "jump").
          const pending = prev.find(
            (r) =>
              r.tool === parsed.tool &&
              (r.data as Record<string, unknown>)?.loading === true,
          );
          const slotTs = pending ? pending.receivedAt : now;
          const base = prev.filter(
            (r) =>
              !(
                r.tool === parsed.tool &&
                (r.data as Record<string, unknown>)?.loading === true
              ),
          );
          if (isLoading) {
            return [...base, { tool: parsed.tool, receivedAt: now, data: parsed.data }];
          }
          if (parsed.tool === "add_to_cart") {
            const cartId = (parsed.data as Record<string, unknown>)?.cartId;
            if (cartId) {
              const hasOld = base.some(
                (r) =>
                  r.tool === "add_to_cart" &&
                  !r.exiting &&
                  (r.data as Record<string, unknown>)?.cartId === cartId,
              );
              const marked = hasOld
                ? base.map((r) =>
                    r.tool === "add_to_cart" &&
                    !r.exiting &&
                    (r.data as Record<string, unknown>)?.cartId === cartId
                      ? { ...r, exiting: true }
                      : r,
                  )
                : base.filter(
                    (r) =>
                      !(
                        r.tool === "add_to_cart" &&
                        r.exiting &&
                        (r.data as Record<string, unknown>)?.cartId === cartId
                      ),
                  );
              if (hasOld) {
                setTimeout(() => {
                  setResults((cur) =>
                    cur.filter(
                      (r) =>
                        !(
                          r.tool === "add_to_cart" &&
                          r.exiting &&
                          (r.data as Record<string, unknown>)?.cartId === cartId
                        ),
                    ),
                  );
                }, 350);
              }
              const items = (parsed.data as Record<string, unknown>)?.items;
              if (!Array.isArray(items) || items.length === 0) return marked;
              return [
                ...marked,
                { tool: parsed.tool, receivedAt: slotTs, data: parsed.data, ui: asUiPayload(parsed.ui), resultAt: now },
              ];
            }
          }
          return [
            ...base,
            { tool: parsed.tool, receivedAt: slotTs, data: parsed.data, ui: asUiPayload(parsed.ui), resultAt: now },
          ];
          });
        if (isLoading) {
          // Skeleton shows immediately; remember when, to enforce the minimum.
          skeletonStartRef.current[parsed.tool] = now;
          commit();
        } else {
          // Real result: if a skeleton is up, hold until it's shown MIN_SKELETON_MS.
          const startedAt = skeletonStartRef.current[parsed.tool];
          delete skeletonStartRef.current[parsed.tool];
          const wait = startedAt
            ? Math.max(0, MIN_SKELETON_MS - (now - startedAt))
            : 0;
          if (wait > 0) {
            // Stamp the arrival on the skeleton now; the data swaps in after the hold.
            setResults((prev) =>
              prev.map((r) =>
                r.tool === parsed.tool && (r.data as Record<string, unknown>)?.loading === true
                  ? { ...r, resultAt: now }
                  : r,
              ),
            );
            setTimeout(commit, wait);
          } else commit();
        }
      } catch (err) {
        // TODO: surface to telemetry when wiring is added.
        console.error("[useToolResults] failed to parse data payload", err);
      }
    };

    const handler = (
      payload: Uint8Array,
      _participant: unknown,
      _kind: unknown,
      topic?: string,
    ) => {
      if (topic !== TOPIC) return;
      handleText(new TextDecoder().decode(payload));
    };

    room.on(RoomEvent.DataReceived, handler);
    let streamRegistered = false;
    try {
      room.registerTextStreamHandler(STREAM_TOPIC, async (reader) => {
        try {
          handleText(await reader.readAll());
        } catch (err) {
          console.error("[useToolResults] failed to read result stream", err);
        }
      });
      streamRegistered = true;
    } catch (err) {
      // Already registered (a second mount on the same room): the first one handles it.
      console.warn("[useToolResults] stream handler not registered", err);
    }
    return () => {
      room.off(RoomEvent.DataReceived, handler);
      if (streamRegistered) room.unregisterTextStreamHandler(STREAM_TOPIC);
    };
  }, [room]);

  return results;
}
